import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box, Typography, Button, TextField, Chip, CircularProgress, alpha, Tooltip, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Collapse, LinearProgress, MenuItem, Dialog, DialogContent, Divider,
} from "@mui/material";
import PageLoader from "../../Components/Loader/PageLoader";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import InboxIcon from "@mui/icons-material/Inbox";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InventoryIcon from "@mui/icons-material/Inventory";
import BarChartIcon from "@mui/icons-material/BarChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNotifier } from "../../Core/Notifier";
import * as XLSX from "xlsx";
import PrintIcon from "@mui/icons-material/Print";
import logoBlack from "../../Images/logo_main_black.webp";
// dayjs imported above with Dayjs type

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const ACCENT = "#0B1E2D"; // DCC navy
const ROUTE_COLORS = ["#3b82f6", "#8b5cf6", "#059669", "#ea580c", "#ec4899", "#0891b2", "#d97706", "#6366f1", "#14b8a6", "#f43f5e"];

interface OrderItem { product_id: number | null; name: string; quantity: number; price: number }
interface PlannerOrder {
  id: number; shop_id: number; shop_name: string; shop_city: string; shop_address: string;
  shop_route_ids: number[]; total: number; status: string; item_count: number;
  items: OrderItem[]; notes: string | null; portal_note: string | null; created_at: string;
}
interface PlannerRoute { id: number; name: string; cities: string[] }
interface Assignment { id: number; route_id: number; shop_order_id: number; status: string }
interface ProductStockMap { [pid: number]: { name: string; stock: number; unit: string; supplier_id?: number | null; supplier_name?: string | null; supplier_price?: number | null } }

// Demand types
interface DemandProduct {
  product_id: number; product_name: string; unit: string; total_quantity: number;
  current_stock: number; shortage: number;
  supplier_id?: number | null; supplier_name?: string | null; supplier_price?: number | null;
  routes: { route_id: number | null; route_name: string; quantity: number }[];
}

const Planner: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState<PlannerRoute[]>([]);
  const [orders, setOrders] = useState<PlannerOrder[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [productStock, setProductStock] = useState<ProductStockMap>({});
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [demandView, setDemandView] = useState<"combined" | "route">("combined");
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [showOnlyShortage, setShowOnlyShortage] = useState(false);
  const [viewOrder, setViewOrder] = useState<PlannerOrder | null>(null);
  const [sortBy, setSortBy] = useState<"shortage" | "demand" | "name">("shortage");
  const demandRef = React.useRef<HTMLDivElement>(null);
  const [poPreviewOpen, setPOPreviewOpen] = useState(false);
  const [poCreating, setPOCreating] = useState(false);

  const getHeaders = () => ({
    "Content-Type": "application/json", Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/delivery/planner-data?date=${date}`, { headers: getHeaders() });
      const json = await res.json();
      if (json.status && json.data) {
        setRoutes(json.data.routes || []);
        setOrders(json.data.orders || []);
        setAssignments(json.data.assignments || []);
        setProductStock(json.data.product_stock || {});
      }
    } catch { showErrorMessage("Failed to load planner data"); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { loadData(); }, [loadData]);

  const assignmentMap = useMemo(() => {
    const m: Record<number, number> = {};
    assignments.forEach(a => { m[a.shop_order_id] = a.route_id; });
    return m;
  }, [assignments]);

  const unassigned = useMemo(() => orders.filter(o => !assignmentMap[o.id]), [orders, assignmentMap]);
  const routeOrders = useMemo(() => {
    const m: Record<number, PlannerOrder[]> = {};
    routes.forEach(r => { m[r.id] = []; });
    orders.forEach(o => { const rid = assignmentMap[o.id]; if (rid && m[rid]) m[rid].push(o); });
    return m;
  }, [orders, routes, assignmentMap]);

  const routeStats = useMemo(() => {
    const m: Record<number, { count: number; total: number }> = {};
    routes.forEach(r => { const ro = routeOrders[r.id] || []; m[r.id] = { count: ro.length, total: ro.reduce((a, o) => a + o.total, 0) }; });
    return m;
  }, [routes, routeOrders]);

  // ─── DEMAND: computed from CURRENT assignments ────────────────────────────
  const demandProducts = useMemo((): DemandProduct[] => {
    const routeMap: Record<number, string> = {};
    routes.forEach(r => { routeMap[r.id] = r.name; });

    const pm: Record<number, DemandProduct> = {};

    orders.forEach(order => {
      const routeId = assignmentMap[order.id] ?? null;
      const routeName = routeId ? (routeMap[routeId] || "Unknown") : "Unassigned";

      order.items.forEach(item => {
        if (!item.product_id) return;
        const pid = item.product_id;
        const ps = productStock[pid];

        if (!pm[pid]) {
          pm[pid] = {
            product_id: pid, product_name: ps?.name || item.name, unit: ps?.unit || "-",
            total_quantity: 0, current_stock: ps?.stock ?? 0, shortage: 0,
            supplier_id: ps?.supplier_id, supplier_name: ps?.supplier_name, supplier_price: ps?.supplier_price,
            routes: [],
          };
        }
        pm[pid].total_quantity += item.quantity;

        // Route breakdown
        const rKey = routeId ?? -1;
        let existing = pm[pid].routes.find(r => (r.route_id ?? -1) === rKey);
        if (!existing) {
          existing = { route_id: routeId, route_name: routeName, quantity: 0 };
          pm[pid].routes.push(existing);
        }
        existing.quantity += item.quantity;
      });
    });

    return Object.values(pm).map(p => ({
      ...p,
      total_quantity: Math.round(p.total_quantity * 100) / 100,
      shortage: Math.max(0, Math.round((p.total_quantity - p.current_stock) * 100) / 100),
    })).sort((a, b) => b.total_quantity - a.total_quantity);
  }, [orders, assignmentMap, routes, productStock]);

  const demandSummary = useMemo(() => ({
    totalQty: demandProducts.reduce((a, p) => a + p.total_quantity, 0),
    totalProducts: demandProducts.length,
    shortages: demandProducts.filter(p => p.shortage > 0).length,
  }), [demandProducts]);

  const totalShortageQty = useMemo(() => demandProducts.reduce((a, p) => a + p.shortage, 0), [demandProducts]);

  // Filtered + sorted demand
  const filteredDemandProducts = useMemo(() => {
    let list = showOnlyShortage ? demandProducts.filter(p => p.shortage > 0) : demandProducts;
    switch (sortBy) {
      case "shortage": list = [...list].sort((a, b) => b.shortage - a.shortage); break;
      case "demand": list = [...list].sort((a, b) => b.total_quantity - a.total_quantity); break;
      case "name": list = [...list].sort((a, b) => a.product_name.localeCompare(b.product_name)); break;
    }
    return list;
  }, [demandProducts, showOnlyShortage, sortBy]);

  // Route-grouped demand
  const routeGroupedDemand = useMemo(() => {
    const m: Record<string, { route_name: string; products: { name: string; unit: string; qty: number }[] }> = {};
    demandProducts.forEach(p => {
      p.routes.forEach(r => {
        const key = String(r.route_id ?? "unassigned");
        if (!m[key]) m[key] = { route_name: r.route_name, products: [] };
        m[key].products.push({ name: p.product_name, unit: p.unit, qty: r.quantity });
      });
    });
    return Object.values(m).sort((a, b) => a.route_name.localeCompare(b.route_name));
  }, [demandProducts]);

  const totalQtyAllOrders = useMemo(() => orders.reduce((a, o) => a + o.items.reduce((b, i) => b + i.quantity, 0), 0), [orders]);

  // ── Route mismatch modal state ───────────────────────────────────────────
  type PendingMove = { orderId: number; destRouteId: number | null };
  const [routeMismatch, setRouteMismatch] = useState<{ pending: PendingMove; order: PlannerOrder; destRoute: PlannerRoute } | null>(null);
  const [mismatchBusy, setMismatchBusy] = useState<"" | "anyway" | "assign">("");

  // ── Click-card details popup state ───────────────────────────────────────
  const [detailsOrder, setDetailsOrder] = useState<PlannerOrder | null>(null);
  // ── Route popup ──
  const [routePopupId, setRoutePopupId] = useState<number | null>(null);
  const [routePopupSelected, setRoutePopupSelected] = useState<Set<number>>(new Set());

  // Apply a move (planner-only, no shop route changes)
  const applyMove = useCallback(async (orderId: number, destRouteId: number | null) => {
    setAssignments(prev => {
      const filtered = prev.filter(a => a.shop_order_id !== orderId);
      if (destRouteId) filtered.push({ id: 0, route_id: destRouteId, shop_order_id: orderId, status: "assigned" });
      return filtered;
    });
    try {
      await fetch(`${API_URL}/delivery/assign`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ order_id: orderId, route_id: destRouteId, date }) });
    } catch {
      showErrorMessage("Failed to assign");
      loadData();
    }
  }, [date, loadData]);

  // Drag
  const handleDragEnd = async (result: DropResult) => {
    const { draggableId, destination, source } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;
    const orderId = parseInt(draggableId.replace("order-", ""), 10);
    const destRouteId = destination.droppableId === "unassigned" ? null : parseInt(destination.droppableId.replace("route-", ""), 10);
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    // Route-mismatch guard: dropping into a route that's NOT in the restaurant's
    // saved route_ids. Fires even when the restaurant has no routes yet, so the
    // user can choose to assign-and-move on the spot.
    const assigned: number[] = Array.isArray(order.shop_route_ids) ? order.shop_route_ids : [];
    if (destRouteId !== null && !assigned.includes(destRouteId)) {
      const destRoute = routes.find(r => r.id === destRouteId);
      if (destRoute) {
        setRouteMismatch({ pending: { orderId, destRouteId }, order: { ...order, shop_route_ids: assigned }, destRoute });
        return; // Wait for user choice before applying
      }
    }
    await applyMove(orderId, destRouteId);
  };

  // Add a route to a shop's assigned route_ids and refresh the in-memory order
  const assignRouteToShop = useCallback(async (shopId: number, routeId: number, currentRouteIds: number[]) => {
    const next = currentRouteIds.includes(routeId) ? currentRouteIds : [...currentRouteIds, routeId];
    const res = await fetch(`${API_URL}/shops/${shopId}`, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({ route_ids: next }),
    });
    if (!res.ok) throw new Error(`Failed to assign route to shop`);
    setOrders(prev => prev.map(o => o.shop_id === shopId ? { ...o, shop_route_ids: next } : o));
  }, []);

  const handleMismatchAction = async (action: "cancel" | "anyway" | "assign") => {
    if (!routeMismatch) return;
    if (action === "cancel") { setRouteMismatch(null); return; }
    const { pending, order } = routeMismatch;
    if (pending.destRouteId === null) { setRouteMismatch(null); return; }
    try {
      if (action === "assign") {
        setMismatchBusy("assign");
        await assignRouteToShop(order.shop_id, pending.destRouteId, order.shop_route_ids);
      } else {
        setMismatchBusy("anyway");
      }
      await applyMove(pending.orderId, pending.destRouteId);
      setRouteMismatch(null);
      showMessage(action === "assign" ? "Route added to restaurant and order moved" : "Order moved (planner only)");
    } catch (err) {
      showErrorMessage("Action failed — try again");
    } finally {
      setMismatchBusy("");
    }
  };

  // ── Print blade (matches CaCart Orders Report style) ──
  const renderPrintShell = useCallback((opts: {
    title: string;          // e.g. "DELIVERY ROUTE REPORT"
    docTitle: string;       // browser tab title
    filterLine: string;     // e.g. "Route: Durham Route • Date: ..."
    summary: { label: string; value: string | number; cls: "total" | "pending" | "delivered" | "overdue" }[];
    tableHead: string;      // <tr><th>...</th></tr>
    tableBody: string;      // multiple <tr>...</tr>
    tableFoot?: string;     // optional <tfoot>...</tfoot>
  }) => {
    const logoUrl = (logoBlack as string).startsWith("http") || (logoBlack as string).startsWith("data:")
      ? (logoBlack as string)
      : `${window.location.origin}${logoBlack}`;
    const reportDate = dayjs().format("MMMM DD, YYYY");
    const preparedBy = localStorage.getItem("user_email") || localStorage.getItem("email") || "—";

    return `<!DOCTYPE html><html><head><title>${opts.docTitle}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1f2937;background:#fff;padding:36px 40px}
  .report{max-width:1180px;margin:0 auto}
  .report-header{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:18px}
  .header-left .logo{height:46px;width:auto;display:block;margin-bottom:10px}
  .header-left .meta{font-size:12px;color:#475569;line-height:1.7}
  .header-right{text-align:right}
  .header-right .title{font-size:26px;font-weight:800;color:#0B1F3A;letter-spacing:.6px;font-family:Georgia,'Times New Roman',serif}
  .header-right .meta{font-size:12px;color:#475569;line-height:1.7;margin-top:4px}
  .filter-strip{background:#eff4fb;border-top:1px solid #d6e1ef;border-bottom:1px solid #d6e1ef;padding:10px 14px;font-size:12px;color:#0B1F3A;margin-bottom:18px}
  .filter-strip strong{font-weight:700;margin-right:6px}
  .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:18px}
  .sum-card{border:1px solid;padding:18px 20px;border-radius:4px;text-align:center}
  .sum-card .label{font-size:10.5px;font-weight:700;letter-spacing:1.2px;color:#64748b;margin-bottom:8px}
  .sum-card .value{font-size:22px;font-weight:800;font-family:Georgia,'Times New Roman',serif}
  .sum-card.total{background:#f3f6fb;border-color:#dde5f0}.sum-card.total .value{color:#0B1F3A}
  .sum-card.pending{background:#fef6e7;border-color:#f4dca0}.sum-card.pending .value{color:#b45309}
  .sum-card.delivered{background:#eafaf0;border-color:#c5ecd2}.sum-card.delivered .value{color:#15803d}
  .sum-card.overdue{background:#fdecec;border-color:#f6c2c2}.sum-card.overdue .value{color:#b91c1c}
  table{width:100%;border-collapse:collapse;font-size:12.5px;margin-bottom:18px}
  thead th{background:#0B1F3A;color:#fff;font-size:10.5px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;padding:12px 12px;text-align:left;border-bottom:1px solid #0B1F3A}
  thead th.right{text-align:right}
  tbody td{padding:14px 12px;border-bottom:1px solid #e5e7eb;vertical-align:middle}
  tbody td.num{color:#94a3b8;font-size:11px;width:36px}
  tbody td.right{text-align:right}
  tbody td.client{font-weight:600;color:#1f2937}
  tbody td.amt{font-weight:700;color:#1f2937}
  tfoot td{background:#0B1F3A;color:#fff;font-size:12.5px;font-weight:700;padding:14px 12px;letter-spacing:.4px}
  tfoot td.right{text-align:right}
  .section-title{font-size:13px;font-weight:800;color:#0B1F3A;letter-spacing:.5px;text-transform:uppercase;margin:18px 0 8px}
  .divider{height:1px;background:#e5e7eb;margin:28px 0 14px 0}
  .footer{text-align:center;font-size:11px;color:#64748b;padding-top:6px}
  .footer .brand{color:#0B1F3A;font-weight:700}
  @media print{body{padding:18px}}
</style></head><body>
<div class="report">
  <div class="report-header">
    <div class="header-left">
      <img src="${logoUrl}" alt="Mr. Veggie Ltd" class="logo" />
      <div class="meta">
        Mr. Veggie Ltd — Fresh From The Earth<br/>
        support@cacart.com
      </div>
    </div>
    <div class="header-right">
      <div class="title">${opts.title}</div>
      <div class="meta">Date: ${reportDate}<br/>Prepared By: ${preparedBy}</div>
    </div>
  </div>
  <div class="filter-strip"><strong>Report:</strong>${opts.filterLine}</div>
  <div class="summary">
    ${opts.summary.map(s => `<div class="sum-card ${s.cls}"><div class="label">${s.label}</div><div class="value">${s.value}</div></div>`).join("")}
  </div>
  <table>
    <thead>${opts.tableHead}</thead>
    <tbody>${opts.tableBody}</tbody>
    ${opts.tableFoot || ""}
  </table>
  <div class="divider"></div>
  <div class="footer">
    <div><span class="brand">Mr. Veggie Ltd</span> — Smart Inventory & Order Management</div>
    <div>Generated via Mr. Veggie Ltd System &nbsp;•&nbsp; © ${dayjs().format("YYYY")}</div>
  </div>
</div>
<script>window.onload=function(){window.print()}</script>
</body></html>`;
  }, []);

  const openPrint = useCallback((html: string) => {
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) { showErrorMessage("Could not open print window — please allow popups"); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
  }, [showErrorMessage]);

  const printOrder = useCallback((order: PlannerOrder, scope: "order" | "products") => {
    const routeNames = order.shop_route_ids.map(rid => routes.find(r => r.id === rid)?.name).filter(Boolean).join(", ") || "—";
    const totalQty = order.items.reduce((a, i) => a + i.quantity, 0);
    const tableHead = scope === "products"
      ? `<tr><th>#</th><th>Product</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Line Total</th></tr>`
      : `<tr><th>#</th><th>Product</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Subtotal</th></tr>`;
    const tableBody = order.items.map((i, idx) =>
      `<tr><td class="num">${idx + 1}</td><td class="client">${i.name}</td><td class="right">${i.quantity}</td><td class="right">$${(i.price || 0).toFixed(2)}</td><td class="right amt">$${((i.price || 0) * i.quantity).toFixed(2)}</td></tr>`
    ).join("") || `<tr><td colspan="5" style="text-align:center;padding:24px;color:#94a3b8">No items</td></tr>`;
    const tableFoot = `<tfoot><tr><td colspan="4">TOTALS &nbsp;|&nbsp; ${order.items.length} item${order.items.length === 1 ? "" : "s"} · ${totalQty} units</td><td class="right">$${order.total.toFixed(2)}</td></tr></tfoot>`;
    const html = renderPrintShell({
      title: scope === "products" ? "RESTAURANT PRODUCT LIST" : "RESTAURANT ORDER REPORT",
      docTitle: `Mr. Veggie Ltd — ${order.shop_name} (Order #${order.id})`,
      filterLine: ` Restaurant: <strong>${order.shop_name}</strong> • Order: <strong>#${order.id}</strong> • Delivery: <strong>${date}</strong> • Routes: <strong>${routeNames}</strong>`,
      summary: [
        { label: "ORDER #",  value: `#${order.id}`,                         cls: "total" },
        { label: "ITEMS",    value: order.items.length,                     cls: "pending" },
        { label: "QTY",      value: totalQty,                               cls: "delivered" },
        { label: "TOTAL",    value: `$${order.total.toFixed(2)}`,           cls: "overdue" },
      ],
      tableHead, tableBody, tableFoot,
    });
    openPrint(html);
  }, [routes, date, renderPrintShell, openPrint]);

  const printRoute = useCallback((routeId: number, restaurantIds?: number[]) => {
    const route = routes.find(r => r.id === routeId);
    if (!route) return;
    const allRos = routeOrders[routeId] || [];
    const ros = restaurantIds && restaurantIds.length > 0
      ? allRos.filter(o => restaurantIds.includes(o.shop_id))
      : allRos;
    const productAgg: Record<string, { name: string; qty: number }> = {};
    ros.forEach(o => o.items.forEach(i => {
      const key = String(i.product_id ?? i.name);
      productAgg[key] = productAgg[key] || { name: i.name, qty: 0 };
      productAgg[key].qty += i.quantity;
    }));
    const totalAmt = ros.reduce((a, o) => a + o.total, 0);
    const totalItems = ros.reduce((a, o) => a + o.item_count, 0);
    const totalProducts = Object.values(productAgg).reduce((a, p) => a + p.qty, 0);

    // Stops table — one row per restaurant order
    const tableHead = `<tr><th>#</th><th>Restaurant</th><th>City</th><th>Order #</th><th class="right">Items</th><th class="right">Total</th><th>Notes</th></tr>`;
    const tableBody = ros.map((o, idx) =>
      `<tr><td class="num">${idx + 1}</td><td class="client">${o.shop_name}</td><td>${o.shop_city || "—"}</td><td><strong style="color:#0B1F3A">#${o.id}</strong></td><td class="right">${o.item_count}</td><td class="right amt">$${o.total.toFixed(2)}</td><td style="font-size:11px;color:#64748b">${(o.notes || o.portal_note || "").replace(/[<>]/g, "")}</td></tr>`
    ).join("") || `<tr><td colspan="7" style="text-align:center;padding:24px;color:#94a3b8">No stops</td></tr>`;
    const tableFoot = `<tfoot><tr><td colspan="5">TOTALS &nbsp;|&nbsp; ${ros.length} restaurant${ros.length === 1 ? "" : "s"} · ${totalItems} item${totalItems === 1 ? "" : "s"}</td><td class="right">$${totalAmt.toFixed(2)}</td><td></td></tr></tfoot>`;

    // Append a product-summary table after the main one — render extra rows in tableBody under a section header is tricky with the shell, so we inline a second table block.
    const productsHtml = Object.values(productAgg)
      .sort((a, b) => b.qty - a.qty)
      .map((p, idx) => `<tr><td class="num">${idx + 1}</td><td class="client">${p.name}</td><td class="right amt">${p.qty}</td></tr>`).join("");

    const baseHtml = renderPrintShell({
      title: "DELIVERY ROUTE REPORT",
      docTitle: `Mr. Veggie Ltd — ${route.name} (${date})`,
      filterLine: ` Route: <strong>${route.name}</strong> • Date: <strong>${date}</strong>${route.cities?.length ? ` • Cities: <strong>${route.cities.join(", ")}</strong>` : ""}${restaurantIds?.length ? ` • Restaurants: <strong>${restaurantIds.length} selected</strong>` : ""}`,
      summary: [
        { label: "ORDERS",      value: ros.length,                       cls: "total" },
        { label: "RESTAURANTS", value: new Set(ros.map(o => o.shop_id)).size, cls: "pending" },
        { label: "PRODUCTS",    value: totalProducts,                    cls: "delivered" },
        { label: "TOTAL VALUE", value: `$${totalAmt.toFixed(2)}`,        cls: "overdue" },
      ],
      tableHead, tableBody, tableFoot,
    });

    // Inject the Product Summary table just before the divider.
    const productSummaryBlock = `
      <div class="section-title">PRODUCT SUMMARY</div>
      <table>
        <thead><tr><th>#</th><th>Product</th><th class="right">Total Qty</th></tr></thead>
        <tbody>${productsHtml || `<tr><td colspan="3" style="text-align:center;padding:24px;color:#94a3b8">No products</td></tr>`}</tbody>
      </table>
    `;
    const html = baseHtml.replace('<div class="divider"></div>', productSummaryBlock + '<div class="divider"></div>');
    openPrint(html);
  }, [routes, routeOrders, date, renderPrintShell, openPrint]);

  const handleAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      const res = await fetch(`${API_URL}/delivery/auto-assign`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ date }) });
      const json = await res.json();
      showMessage(json.message || "Auto-assigned");
      await loadData();
      setTimeout(() => demandRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    } catch { showErrorMessage("Auto-assign failed"); }
    finally { setAutoAssigning(false); }
  };

  const handleExportDemand = () => {
    const rows = demandProducts.map(p => ({
      Product: p.product_name, Unit: p.unit, "Qty Needed": p.total_quantity,
      "In Stock": p.current_stock, Shortage: p.shortage,
      ...Object.fromEntries(p.routes.map(r => [r.route_name, r.quantity])),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length, 10) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Demand");
    XLSX.writeFile(wb, `Demand_${date}.xlsx`);
  };

  // PO preview: group shortages by supplier
  const poPreviewData = useMemo(() => {
    const shortages = demandProducts.filter(p => p.shortage > 0);
    const grouped: Record<string, { supplier_id: number | null; supplier_name: string; items: { product_id: number; name: string; qty: number; unit: string; price: number }[]; total: number }> = {};
    const noSupplier: string[] = [];

    shortages.forEach(p => {
      if (!p.supplier_id) { noSupplier.push(p.product_name); return; }
      const key = String(p.supplier_id);
      if (!grouped[key]) grouped[key] = { supplier_id: p.supplier_id, supplier_name: p.supplier_name || "Unknown", items: [], total: 0 };
      const lineTotal = p.shortage * (p.supplier_price || 0);
      grouped[key].items.push({ product_id: p.product_id, name: p.product_name, qty: p.shortage, unit: p.unit, price: p.supplier_price || 0 });
      grouped[key].total += lineTotal;
    });

    return { suppliers: Object.values(grouped), noSupplier };
  }, [demandProducts]);

  const handleCreatePO = (productId: number, productName: string, qty: number, unit: string) => {
    // Single product — open preview modal with just this one
    setPOPreviewOpen(true);
  };

  const handleGeneratePOs = () => { setPOPreviewOpen(true); };

  const handleConfirmPOs = async () => {
    setPOCreating(true);
    try {
      const shortages = demandProducts.filter(p => p.shortage > 0 && p.supplier_id);
      const res = await fetch(`${API_URL}/procurement/auto-po-from-demand`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({
          date,
          products: shortages.map(p => ({ product_id: p.product_id, shortage: p.shortage })),
        }),
      });
      const json = await res.json();
      if (json.status) {
        showMessage(`${json.pos_created} Purchase Order(s) created`);
        if (json.no_supplier?.length > 0) showErrorMessage(`No supplier: ${json.no_supplier.join(", ")}`);
        setPOPreviewOpen(false);
      } else {
        showErrorMessage(json.message || "Failed to create POs");
      }
    } catch { showErrorMessage("Failed to create Purchase Orders"); }
    finally { setPOCreating(false); }
  };

  const scrollToShortages = () => {
    setShowOnlyShortage(true);
    setSortBy("shortage");
    demandRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleExpand = (pid: number) => setExpandedRows(prev => { const n = new Set(prev); n.has(pid) ? n.delete(pid) : n.add(pid); return n; });
  const getRouteColor = (idx: number) => ROUTE_COLORS[idx % ROUTE_COLORS.length];

  if (loading) return <PageLoader />;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, minHeight: "100vh", bgcolor: "#F8FAFC" }}>

      {/* ═══ TOP BAR ═══ */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3,
        p: 2.5, borderRadius: "16px", bgcolor: "#fff", border: "1px solid #EEF2F7", boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${ACCENT}, #071521)`, boxShadow: `0 4px 14px ${alpha(ACCENT, 0.3)}`, border: "1px solid rgba(212,175,55,0.35)" }}>
            <LocalShippingIcon sx={{ fontSize: 22, color: "#D4AF37" }} />
          </Box>
          <Box>
            <Typography fontSize={20} fontWeight={800} color="#0f172a" letterSpacing="-0.02em">Delivery Planner</Typography>
            <Typography fontSize={12} color="#94a3b8">
              {orders.length} orders · {unassigned.length} unassigned · {Math.round(totalQtyAllOrders)} total qty
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* Date nav: prev / picker / next / today */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0,
            bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            overflow: "hidden",
          }}>
            <IconButton size="small" onClick={() => setDate(dayjs(date).subtract(1, "day").format("YYYY-MM-DD"))}
              sx={{ borderRadius: 0, width: 34, height: 36, color: "#94a3b8", "&:hover": { bgcolor: alpha(ACCENT, 0.04), color: ACCENT } }}>
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dayjs(date)}
                onChange={(val: Dayjs | null) => { if (val) setDate(val.format("YYYY-MM-DD")); }}
                format="ddd, MMM D"
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "standard",
                    InputProps: {
                      disableUnderline: true,
                      startAdornment: <CalendarTodayIcon sx={{ fontSize: 15, color: ACCENT, mr: 0.75 }} />,
                      sx: {
                        fontSize: 13, fontWeight: 700, color: "#0f172a",
                        px: 1, py: 0.5, cursor: "pointer",
                        minWidth: 140, textAlign: "center",
                      },
                    },
                  },
                  popper: {
                    sx: {
                      "& .MuiPaper-root": {
                        borderRadius: 3, border: "1px solid #e2e8f0",
                        boxShadow: "0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
                        mt: 1,
                      },
                      "& .MuiPickersDay-root": {
                        borderRadius: 2, fontSize: 13, fontWeight: 500,
                        "&.Mui-selected": { bgcolor: ACCENT, fontWeight: 700, "&:hover": { bgcolor: "#006d7a" } },
                        "&:hover": { bgcolor: alpha(ACCENT, 0.08) },
                      },
                      "& .MuiPickersDay-today": {
                        border: `1px solid ${alpha(ACCENT, 0.4)}`,
                      },
                      "& .MuiPickersCalendarHeader-label": { fontSize: 14, fontWeight: 700, color: "#0f172a" },
                      "& .MuiDayCalendar-weekDayLabel": { fontSize: 11, fontWeight: 600, color: "#94a3b8" },
                      "& .MuiPickersArrowSwitcher-button": { color: "#64748b" },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            <IconButton size="small" onClick={() => setDate(dayjs(date).add(1, "day").format("YYYY-MM-DD"))}
              sx={{ borderRadius: 0, width: 34, height: 36, color: "#94a3b8", "&:hover": { bgcolor: alpha(ACCENT, 0.04), color: ACCENT } }}>
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Today button */}
          <Tooltip title="Jump to today">
            <IconButton size="small" onClick={() => setDate(dayjs().format("YYYY-MM-DD"))}
              sx={{
                width: 36, height: 36, borderRadius: 2.5,
                bgcolor: date === dayjs().format("YYYY-MM-DD") ? alpha(ACCENT, 0.08) : "#fff",
                border: `1px solid ${date === dayjs().format("YYYY-MM-DD") ? alpha(ACCENT, 0.3) : "#e2e8f0"}`,
                color: date === dayjs().format("YYYY-MM-DD") ? ACCENT : "#94a3b8",
                "&:hover": { bgcolor: alpha(ACCENT, 0.08), borderColor: ACCENT, color: ACCENT },
                transition: "all 0.2s",
              }}>
              <TodayIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Button size="small" variant="outlined" startIcon={autoAssigning ? <CircularProgress size={14} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
            onClick={handleAutoAssign} disabled={autoAssigning || unassigned.length === 0}
            sx={{ borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, borderColor: "#e2e8f0", color: "#7c3aed", "&:hover": { borderColor: "#7c3aed", bgcolor: alpha("#7c3aed", 0.04) } }}>
            Auto Assign
          </Button>
        </Box>
      </Box>

      {/* ═══ SECTION 1: KANBAN ═══ */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={{ display: "flex", gap: 2.5, overflow: "auto", pb: 2, alignItems: "flex-start", mb: 4 }}>
          {/* Unassigned Column */}
          <Box sx={{ minWidth: 300, maxWidth: 320, flexShrink: 0 }}>
            <Box sx={{ p: 2, borderRadius: "16px", bgcolor: "#fff", border: "1px solid #EEF2F7", boxShadow: "0 4px 14px rgba(0,0,0,0.05)" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <InboxIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Unassigned</Typography>
                </Box>
                <Chip label={unassigned.length} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: "#f1f5f9", color: "#64748b" }} />
              </Box>
              <Droppable droppableId="unassigned">
                {(provided, snapshot) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}
                    sx={{ minHeight: 80, borderRadius: 2, p: 0.5, bgcolor: snapshot.isDraggingOver ? alpha(ACCENT, 0.04) : "transparent", border: snapshot.isDraggingOver ? `2px dashed ${alpha(ACCENT, 0.3)}` : "2px dashed transparent", transition: "all 0.2s" }}>
                    {unassigned.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}><CheckCircleIcon sx={{ fontSize: 36, color: "#d1fae5", mb: 1 }} /><Typography fontSize={12} color="#94a3b8">All orders assigned</Typography></Box>
                    ) : unassigned.map((order, idx) => (
                      <Draggable key={order.id} draggableId={`order-${order.id}`} index={idx}>
                        {(prov, snap) => (
                          <Box ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                            onClick={() => { if (!snap.isDragging) setViewOrder(order); }}
                            sx={{
                              p: 1.5, mb: 1, borderRadius: 2.5, bgcolor: "#fff",
                              border: snap.isDragging ? `2px solid ${ACCENT}` : "1px solid #e2e8f0",
                              boxShadow: snap.isDragging ? `0 8px 24px ${alpha(ACCENT, 0.15)}` : "0 1px 2px rgba(0,0,0,0.02)",
                              cursor: snap.isDragging ? "grabbing" : "pointer",
                              transition: snap.isDragging ? "none" : "all 0.2s ease",
                              "&:hover": snap.isDragging ? {} : {
                                borderColor: alpha(ACCENT, 0.35),
                                transform: "translateY(-2px)",
                                boxShadow: `0 8px 24px ${alpha(ACCENT, 0.10)}`,
                              },
                            }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                              <DragIndicatorIcon sx={{ fontSize: 16, color: "#cbd5e1", mt: 0.25, flexShrink: 0 }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography fontSize={12} fontWeight={700} color="#0f172a" noWrap>{order.shop_name}</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                  <Chip label={`$${order.total.toFixed(2)}`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: "#ecfdf5", color: "#059669" }} />
                                  <Chip label={`${order.item_count} items`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} />
                                </Box>
                                {order.shop_route_ids.length > 0 && (
                                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                                    {order.shop_route_ids.slice(0, 2).map(rid => { const r = routes.find(rt => rt.id === rid); return r ? <Chip key={rid} label={r.name} size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: alpha(ACCENT, 0.08), color: ACCENT }} /> : null; })}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Box>
          </Box>

          {/* Route Columns */}
          {routes.map((route, rIdx) => {
            const color = getRouteColor(rIdx);
            const stats = routeStats[route.id] || { count: 0, total: 0 };
            const rOrders = routeOrders[route.id] || [];
            return (
              <Box key={route.id} sx={{ minWidth: 280, maxWidth: 320, flexShrink: 0 }}>
                <Box sx={{ borderRadius: "16px", bgcolor: "#fff", border: "1px solid #EEF2F7", boxShadow: "0 4px 14px rgba(0,0,0,0.05)", overflow: "hidden", transition: "all 0.2s", "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 22px rgba(0,0,0,0.08)" } }}>
                  <Box
                    onClick={() => { setRoutePopupId(route.id); setRoutePopupSelected(new Set((routeOrders[route.id] || []).map(o => o.shop_id))); }}
                    sx={{ p: 2, borderBottom: `3px solid ${color}`, background: `linear-gradient(135deg, ${alpha(color, 0.03)} 0%, transparent 100%)`, cursor: "pointer", "&:hover": { background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, transparent 100%)` } }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: color }}><AltRouteIcon sx={{ fontSize: 15, color: "#fff" }} /></Box>
                        <Typography fontSize={14} fontWeight={700} color="#0f172a">{route.name}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={`Print ${route.name}`}>
                          <IconButton size="small" onClick={() => printRoute(route.id)}
                            disabled={stats.count === 0}
                            sx={{ width: 22, height: 22, color: stats.count === 0 ? "#cbd5e1" : "#475569", "&:hover": { color, bgcolor: alpha(color, 0.08) } }}>
                            <PrintIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                        <Chip label={stats.count} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: alpha(color, 0.1), color }} />
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Box><Typography fontSize={9} color="#94a3b8" fontWeight={600} textTransform="uppercase">Orders</Typography><Typography fontSize={16} fontWeight={800} color="#0f172a">{stats.count}</Typography></Box>
                      <Box><Typography fontSize={9} color="#94a3b8" fontWeight={600} textTransform="uppercase">Value</Typography><Typography fontSize={16} fontWeight={800} color={color}>${stats.total.toFixed(2)}</Typography></Box>
                    </Box>
                  </Box>
                  <Droppable droppableId={`route-${route.id}`}>
                    {(provided, snapshot) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}
                        sx={{ minHeight: 80, p: 1, m: 1, borderRadius: 2, bgcolor: snapshot.isDraggingOver ? alpha(color, 0.04) : "transparent", border: snapshot.isDraggingOver ? `2px dashed ${alpha(color, 0.3)}` : "2px dashed transparent", transition: "all 0.2s" }}>
                        {rOrders.length === 0 && !snapshot.isDraggingOver && (
                          <Box sx={{ textAlign: "center", py: 3 }}><ShoppingBagIcon sx={{ fontSize: 28, color: "#e2e8f0", mb: 0.5 }} /><Typography fontSize={11} color="#cbd5e1">Drop orders here</Typography></Box>
                        )}
                        {rOrders.map((order, idx) => (
                          <Draggable key={order.id} draggableId={`order-${order.id}`} index={idx}>
                            {(prov, snap) => (
                              <Box ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                                onClick={() => { if (!snap.isDragging) setViewOrder(order); }}
                                sx={{
                                  p: 1.5, mb: 0.75, borderRadius: 2, bgcolor: "#fff",
                                  border: snap.isDragging ? `2px solid ${color}` : `1px solid ${alpha(color, 0.15)}`,
                                  borderLeft: `3px solid ${color}`,
                                  boxShadow: snap.isDragging ? `0 8px 24px ${alpha(color, 0.15)}` : "none",
                                  cursor: snap.isDragging ? "grabbing" : "pointer",
                                  transition: snap.isDragging ? "none" : "all 0.2s ease",
                                  "&:hover": snap.isDragging ? {} : {
                                    bgcolor: alpha(color, 0.04),
                                    borderColor: alpha(color, 0.45),
                                    transform: "translateY(-2px)",
                                    boxShadow: `0 6px 18px ${alpha(color, 0.12)}`,
                                  },
                                }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <DragIndicatorIcon sx={{ fontSize: 14, color: "#cbd5e1", flexShrink: 0 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography fontSize={11} fontWeight={700} color="#0f172a" noWrap>{order.shop_name}</Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                                      <Typography fontSize={11} fontWeight={700} color={color}>${order.total.toFixed(2)}</Typography>
                                      <Typography fontSize={9} color="#94a3b8">{order.item_count} items</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              </Box>
            );
          })}
          {routes.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8, px: 4, flex: 1 }}><AltRouteIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1 }} /><Typography fontSize={16} fontWeight={600} color="#94a3b8">No routes configured</Typography></Box>
          )}
        </Box>
      </DragDropContext>

      {/* ═══ SECTION 2: DEMAND PLANNER (Premium AI-style redesign) ═══ */}
      <Box ref={demandRef} sx={{ mt: 1 }}>

        {/* Premium Header */}
        <Box sx={{
          position: "relative",
          p: 3, mb: 2, borderRadius: 3,
          background: "linear-gradient(135deg, #0B1E2D 0%, #1E293B 50%, #4C1D95 100%)",
          color: "#fff",
          boxShadow: "0 12px 32px rgba(11,30,45,0.20)",
          overflow: "hidden",
        }}>
          {/* Decorative blur orbs */}
          <Box sx={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 70%)", filter: "blur(40px)" }} />
          <Box sx={{ position: "absolute", bottom: -50, left: 100, width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(212,165,55,0.18) 0%, transparent 70%)", filter: "blur(50px)" }} />
          <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #7c3aed, #6d28d9)", boxShadow: "0 8px 20px rgba(124,58,237,0.45)" }}>
              <BarChartIcon sx={{ fontSize: 28, color: "#fff" }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography fontSize={22} fontWeight={800} letterSpacing="-0.02em">Demand & Procurement Intelligence</Typography>
                <Chip label="LIVE" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 800, bgcolor: alpha("#22d3ee", 0.18), color: "#22d3ee", letterSpacing: 0.6 }} />
              </Box>
              <Typography fontSize={12} sx={{ color: "rgba(255,255,255,0.55)" }}>AI-powered analytics from today's delivery plan · Updates instantly as you assign orders</Typography>
            </Box>
            <Box sx={{ display: "flex", bgcolor: alpha("#000", 0.25), borderRadius: 2, p: 0.25 }}>
              <Tooltip title="Combined View"><IconButton size="small" onClick={() => setDemandView("combined")} sx={{ borderRadius: 1.5, bgcolor: demandView === "combined" ? alpha("#fff", 0.15) : "transparent", color: demandView === "combined" ? "#fff" : "rgba(255,255,255,0.5)" }}><ViewListIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
              <Tooltip title="Route View"><IconButton size="small" onClick={() => setDemandView("route")} sx={{ borderRadius: 1.5, bgcolor: demandView === "route" ? alpha("#fff", 0.15) : "transparent", color: demandView === "route" ? "#fff" : "rgba(255,255,255,0.5)" }}><AccountTreeIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            </Box>
          </Box>

          {/* Analytics KPI cards */}
          {(() => {
            const totalProducts = demandSummary.totalProducts;
            const critical = demandSummary.shortages;
            const healthy = totalProducts - critical;
            const estCost = demandProducts
              .filter(p => p.shortage > 0 && p.supplier_price)
              .reduce((s, p) => s + p.shortage * (p.supplier_price || 0), 0);
            const cards = [
              { label: "Products Needed",     value: totalProducts,                                                 sub: `${Math.round(demandSummary.totalQty)} total units`,  icon: <InventoryIcon sx={{ fontSize: 20, color: "#fff" }} />,        gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)", glow: "rgba(59,130,246,0.32)" },
              { label: "Critical Shortages",  value: critical,                                                      sub: critical > 0 ? "Action required" : "All clear",        icon: <WarningAmberIcon sx={{ fontSize: 20, color: "#fff" }} />,    gradient: "linear-gradient(135deg, #ef4444, #b91c1c)", glow: "rgba(239,68,68,0.32)", urgent: critical > 0 },
              { label: "Healthy Inventory",   value: healthy,                                                       sub: totalProducts ? `${Math.round((healthy / totalProducts) * 100)}% coverage` : "—", icon: <CheckCircleIcon sx={{ fontSize: 20, color: "#fff" }} />, gradient: "linear-gradient(135deg, #10b981, #047857)", glow: "rgba(16,185,129,0.32)" },
              { label: "Estimated Cost",      value: `$${estCost.toFixed(0)}`,                                      sub: critical > 0 ? `${critical} POs to generate` : "No purchase needed", icon: <ShoppingCartIcon sx={{ fontSize: 20, color: "#fff" }} />, gradient: "linear-gradient(135deg, #d4a017, #b8860b)", glow: "rgba(212,160,23,0.32)" },
            ];
            return (
              <Box sx={{ position: "relative", display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
                {cards.map((c) => (
                  <Box key={c.label} sx={{
                    p: 1.75, borderRadius: 2.5,
                    bgcolor: alpha("#fff", 0.06),
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${alpha("#fff", 0.08)}`,
                    transition: "all 0.2s ease",
                    "&:hover": { transform: "translateY(-2px)", bgcolor: alpha("#fff", 0.08), boxShadow: `0 8px 24px ${c.glow}` },
                  }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", background: c.gradient, boxShadow: `0 6px 14px ${c.glow}` }}>
                        {c.icon}
                      </Box>
                      {(c as any).urgent && <Chip label="URGENT" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 800, bgcolor: alpha("#ef4444", 0.18), color: "#fca5a5", letterSpacing: 0.6 }} />}
                    </Box>
                    <Typography fontSize={10} fontWeight={700} sx={{ color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{c.label}</Typography>
                    <Typography fontSize={24} fontWeight={800} sx={{ color: "#fff", lineHeight: 1.2, fontFamily: "Georgia,'Times New Roman',serif" }}>{c.value}</Typography>
                    <Typography fontSize={10} sx={{ color: "rgba(255,255,255,0.5)", mt: 0.25 }}>{c.sub}</Typography>
                  </Box>
                ))}
              </Box>
            );
          })()}
        </Box>

        {/* Sticky glass toolbar */}
        {demandProducts.length > 0 && demandView === "combined" && (
          <Box sx={{
            position: "sticky", top: 0, zIndex: 5,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1,
            px: 2.5, py: 1.25, mb: 1.5,
            borderRadius: "14px",
            bgcolor: alpha("#ffffff", 0.85),
            backdropFilter: "blur(12px)",
            border: `1px solid ${alpha("#0B1E2D", 0.08)}`,
            boxShadow: "0 4px 18px rgba(11,30,45,0.06)",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={showOnlyShortage ? "Critical Only" : "All Products"}
                icon={showOnlyShortage ? <WarningAmberIcon sx={{ fontSize: 13 }} /> : <ShoppingCartIcon sx={{ fontSize: 13 }} />}
                onClick={() => setShowOnlyShortage(!showOnlyShortage)}
                size="small"
                sx={{ height: 30, fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: "8px",
                  bgcolor: showOnlyShortage ? "#fef2f2" : "#f1f5f9",
                  color: showOnlyShortage ? "#dc2626" : "#64748b",
                  border: showOnlyShortage ? "1px solid #fecaca" : "1px solid #e2e8f0",
                  "&:hover": { bgcolor: showOnlyShortage ? "#fee2e2" : "#e2e8f0" },
                }} />
              <TextField select size="small" value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}
                sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 11, height: 30, "& fieldset": { borderColor: "#e2e8f0" } } }}>
                <MenuItem value="shortage">Sort: Highest Shortage</MenuItem>
                <MenuItem value="demand">Sort: Highest Demand</MenuItem>
                <MenuItem value="name">Sort: Product Name</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="outlined" startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />} onClick={handleExportDemand}
                sx={{ borderRadius: "8px", textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#0B1E2D", color: "#0B1E2D" } }}>Export</Button>
              {demandSummary.shortages > 0 && (
                <Button size="small" variant="contained" disableElevation startIcon={<ShoppingCartIcon sx={{ fontSize: 14 }} />} onClick={handleGeneratePOs}
                  sx={{
                    borderRadius: "8px", textTransform: "none", fontSize: 11, fontWeight: 800,
                    px: 1.75,
                    background: "linear-gradient(135deg, #ef4444, #b91c1c)",
                    boxShadow: "0 6px 16px rgba(239,68,68,0.35)",
                    transition: "all 0.2s ease",
                    "&:hover": { background: "linear-gradient(135deg, #dc2626, #991b1b)", transform: "translateY(-1px)", boxShadow: "0 8px 22px rgba(239,68,68,0.45)" },
                  }}>
                  Generate Purchase Orders ({demandSummary.shortages})
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Demand Table */}
        <Box sx={{ bgcolor: "#fff", borderRadius: "0 0 16px 16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          {orders.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <InventoryIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1.5 }} />
              <Typography fontSize={15} fontWeight={600} color="#94a3b8">No demand yet</Typography>
              <Typography fontSize={12} color="#cbd5e1" mt={0.5}>Assign orders above to calculate production requirements</Typography>
            </Box>
          ) : demandProducts.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <InventoryIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1.5 }} />
              <Typography fontSize={15} fontWeight={600} color="#94a3b8">No product data in orders</Typography>
              <Typography fontSize={12} color="#cbd5e1" mt={0.5}>Orders exist but contain no product items</Typography>
            </Box>
          ) : demandView === "combined" ? (
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
              {filteredDemandProducts.map(p => {
                const expanded = expandedRows.has(p.product_id);
                const hasShortage = p.shortage > 0;
                const ratio = p.total_quantity > 0 ? p.current_stock / p.total_quantity : 1;
                const stockPct = Math.max(0, Math.min(100, ratio * 100));
                // AI-style health label
                const health = ratio >= 1     ? { label: "Inventory Healthy",       chipBg: "#ecfdf5", chipFg: "#047857", barColor: "#10b981" }
                             : ratio >= 0.66  ? { label: "Suggested Reorder Soon",  chipBg: "#eff6ff", chipFg: "#1d4ed8", barColor: "#3b82f6" }
                             : ratio >= 0.33  ? { label: "Low — Restock Recommended", chipBg: "#fff7ed", chipFg: "#c2410c", barColor: "#f59e0b" }
                             :                  { label: "Urgent Restock Needed",   chipBg: "#fef2f2", chipFg: "#b91c1c", barColor: "#ef4444" };
                const lineCost = (p.shortage || 0) * (p.supplier_price || 0);
                const initial = (p.product_name || "?").trim().charAt(0).toUpperCase();
                const avatarColor = ROUTE_COLORS[(p.product_id || 0) % ROUTE_COLORS.length];
                return (
                  <Box key={p.product_id} sx={{
                    bgcolor: "#fff",
                    border: `1px solid ${hasShortage ? alpha(health.barColor, 0.3) : "#e2e8f0"}`,
                    borderRadius: 2.5,
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                    "&:hover": { borderColor: alpha(health.barColor, 0.5), boxShadow: `0 8px 22px ${alpha(health.barColor, 0.10)}`, transform: "translateY(-1px)" },
                  }}>
                    {/* Top row */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, cursor: "pointer" }}
                      onClick={() => toggleExpand(p.product_id)}>
                      {/* Avatar */}
                      <Box sx={{
                        width: 44, height: 44, borderRadius: 2,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: `linear-gradient(135deg, ${avatarColor}, ${alpha(avatarColor, 0.7)})`,
                        color: "#fff", fontWeight: 800, fontSize: 18,
                        boxShadow: `0 4px 10px ${alpha(avatarColor, 0.30)}`,
                        flexShrink: 0,
                      }}>{initial}</Box>

                      {/* Name + tags */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                          <Typography fontSize={14} fontWeight={700} color="#0f172a">{p.product_name}</Typography>
                          <Chip label={p.unit} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: "#f1f5f9", color: "#64748b" }} />
                          <Chip label={health.label} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 800, bgcolor: health.chipBg, color: health.chipFg, letterSpacing: "0.02em" }} />
                          {p.routes.length > 0 && (
                            <Chip label={`${p.routes.length} route${p.routes.length === 1 ? "" : "s"}`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: alpha("#7c3aed", 0.08), color: "#7c3aed" }} />
                          )}
                          {p.supplier_name && (
                            <Chip label={p.supplier_name} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha("#0B1E2D", 0.06), color: "#0B1E2D" }} />
                          )}
                        </Box>
                        {/* Health bar */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box sx={{ flex: 1, height: 8, borderRadius: 999, bgcolor: "#f1f5f9", overflow: "hidden" }}>
                            <Box sx={{
                              height: "100%", width: `${stockPct}%`,
                              background: `linear-gradient(90deg, ${alpha(health.barColor, 0.8)}, ${health.barColor})`,
                              borderRadius: 999,
                              transition: "width 0.4s ease",
                              boxShadow: `0 0 8px ${alpha(health.barColor, 0.4)}`,
                            }} />
                          </Box>
                          <Typography fontSize={10} fontWeight={700} color="#64748b" sx={{ minWidth: 38, textAlign: "right" }}>{Math.round(stockPct)}%</Typography>
                        </Box>
                      </Box>

                      {/* Numbers */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexShrink: 0 }}>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography fontSize={9} fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em">Needed</Typography>
                          <Typography fontSize={16} fontWeight={800} color="#0f172a" sx={{ fontFamily: "Georgia,serif" }}>{p.total_quantity}</Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography fontSize={9} fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em">In Stock</Typography>
                          <Typography fontSize={16} fontWeight={800} color={hasShortage ? "#ea580c" : "#059669"} sx={{ fontFamily: "Georgia,serif" }}>{p.current_stock}</Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography fontSize={9} fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em">Shortage</Typography>
                          {hasShortage ? (
                            <Typography fontSize={16} fontWeight={800} color="#dc2626" sx={{ fontFamily: "Georgia,serif" }}>−{p.shortage}</Typography>
                          ) : (
                            <Typography fontSize={14} fontWeight={700} color="#059669">All Set</Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Actions */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                        {hasShortage && (
                          <Button size="small" variant="contained" disableElevation
                            onClick={() => handleCreatePO(p.product_id, p.product_name, p.shortage, p.unit)}
                            startIcon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              borderRadius: "8px", textTransform: "none", fontSize: 11, fontWeight: 800,
                              px: 1.5, py: 0.5,
                              background: "linear-gradient(135deg, #ef4444, #b91c1c)",
                              boxShadow: "0 4px 12px rgba(239,68,68,0.30)",
                              "&:hover": { background: "linear-gradient(135deg, #dc2626, #991b1b)", transform: "translateY(-1px)" },
                            }}>
                            Create PO{lineCost > 0 ? ` · $${lineCost.toFixed(0)}` : ""}
                          </Button>
                        )}
                        <IconButton size="small" sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: alpha("#0B1E2D", 0.05), color: "#0B1E2D", "&:hover": { bgcolor: alpha("#0B1E2D", 0.10) } }}>
                          {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Expanded panel */}
                    <Collapse in={expanded}>
                      <Box sx={{ borderTop: "1px solid #e2e8f0", bgcolor: "#fafbfd", px: 2, py: 1.75, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                        {/* Route breakdown */}
                        <Box>
                          <Typography fontSize={10} fontWeight={800} color="#94a3b8" textTransform="uppercase" letterSpacing="0.08em" mb={1}>Route Breakdown</Typography>
                          {p.routes.length === 0 ? (
                            <Typography fontSize={12} color="#94a3b8">No route assignments yet</Typography>
                          ) : p.routes.map((r, ri) => {
                            const rPct = p.total_quantity > 0 ? (r.quantity / p.total_quantity) * 100 : 0;
                            return (
                              <Box key={ri} sx={{ mb: 0.75 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.25 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ROUTE_COLORS[ri % ROUTE_COLORS.length] }} />
                                    <Typography fontSize={12} fontWeight={600} color="#475569">{r.route_name}</Typography>
                                  </Box>
                                  <Typography fontSize={12} fontWeight={800} color="#0f172a">{r.quantity} {p.unit}</Typography>
                                </Box>
                                <Box sx={{ height: 4, borderRadius: 999, bgcolor: "#f1f5f9", overflow: "hidden" }}>
                                  <Box sx={{ height: "100%", width: `${rPct}%`, bgcolor: ROUTE_COLORS[ri % ROUTE_COLORS.length], borderRadius: 999, transition: "width 0.4s ease" }} />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                        {/* Procurement insight */}
                        <Box>
                          <Typography fontSize={10} fontWeight={800} color="#94a3b8" textTransform="uppercase" letterSpacing="0.08em" mb={1}>Procurement Insight</Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography fontSize={12} color="#64748b">Recommended order</Typography><Typography fontSize={12} fontWeight={800} color={hasShortage ? "#dc2626" : "#059669"}>{hasShortage ? `${p.shortage} ${p.unit}` : "None"}</Typography></Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography fontSize={12} color="#64748b">Preferred supplier</Typography><Typography fontSize={12} fontWeight={700} color="#0f172a">{p.supplier_name || "Not set"}</Typography></Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography fontSize={12} color="#64748b">Last cost</Typography><Typography fontSize={12} fontWeight={700} color="#0f172a">{p.supplier_price ? `$${Number(p.supplier_price).toFixed(2)}/${p.unit}` : "—"}</Typography></Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography fontSize={12} color="#64748b">Estimated cost</Typography><Typography fontSize={13} fontWeight={800} color={hasShortage ? "#b91c1c" : "#059669"}>${(lineCost || 0).toFixed(2)}</Typography></Box>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              {routeGroupedDemand.map((group, gi) => (
                <Box key={gi} sx={{ mb: 2, borderRadius: 2.5, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <Box sx={{ px: 2, py: 1.25, borderBottom: `3px solid ${ROUTE_COLORS[gi % ROUTE_COLORS.length]}`, background: `linear-gradient(135deg, ${alpha(ROUTE_COLORS[gi % ROUTE_COLORS.length], 0.04)} 0%, transparent 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: ROUTE_COLORS[gi % ROUTE_COLORS.length] }}><AltRouteIcon sx={{ fontSize: 13, color: "#fff" }} /></Box>
                      <Typography fontSize={13} fontWeight={700}>{group.route_name}</Typography>
                    </Box>
                    <Chip label={`${group.products.length} products`} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: alpha(ROUTE_COLORS[gi % ROUTE_COLORS.length], 0.1), color: ROUTE_COLORS[gi % ROUTE_COLORS.length] }} />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow sx={{ bgcolor: "#f8fafc" }}>
                        {["Product", "Unit", "Quantity"].map(h => <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", py: 1, textTransform: "uppercase" }}>{h}</TableCell>)}
                      </TableRow></TableHead>
                      <TableBody>
                        {group.products.map((p, pi) => (
                          <TableRow key={pi} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                            <TableCell sx={{ fontSize: 12, fontWeight: 500, py: 1 }}>{p.name}</TableCell>
                            <TableCell sx={{ py: 1 }}><Chip label={p.unit} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} /></TableCell>
                            <TableCell sx={{ fontSize: 13, fontWeight: 700, py: 1 }}>{p.qty}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      {/* ═══ ORDER DETAIL MODAL ═══ */}
      {/* ═══ ROUTE POPUP (route-wise restaurants + product summary + print) ═══ */}
      <Dialog
        open={routePopupId !== null}
        onClose={() => setRoutePopupId(null)}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {routePopupId !== null && (() => {
          const route = routes.find(r => r.id === routePopupId);
          if (!route) return null;
          const ros = routeOrders[routePopupId] || [];
          const shopGroups = Array.from(new Map(ros.map(o => [o.shop_id, o])).values()); // unique-by-shop, first order per shop
          const productAgg: Record<string, { name: string; qty: number }> = {};
          ros.forEach(o => o.items.forEach(i => {
            const key = String(i.product_id ?? i.name);
            productAgg[key] = productAgg[key] || { name: i.name, qty: 0 };
            productAgg[key].qty += i.quantity;
          }));
          const totalAmt = ros.reduce((a, o) => a + o.total, 0);
          const totalProducts = Object.values(productAgg).reduce((a, p) => a + p.qty, 0);
          const allChecked = shopGroups.length > 0 && shopGroups.every(o => routePopupSelected.has(o.shop_id));
          const someChecked = shopGroups.some(o => routePopupSelected.has(o.shop_id)) && !allChecked;
          return (
            <>
              <Box sx={{ background: "linear-gradient(135deg, #0B1F3A 0%, #11315F 100%)", px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <AltRouteIcon sx={{ fontSize: 22, color: "#fff" }} />
                  <Box>
                    <Typography fontSize={15} fontWeight={800} color="#fff">{route.name}</Typography>
                    <Typography fontSize={11} color="rgba(255,255,255,0.6)">{date} · {ros.length} order{ros.length === 1 ? "" : "s"} · ${totalAmt.toFixed(2)}</Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setRoutePopupId(null)} sx={{ color: "rgba(255,255,255,0.65)", "&:hover": { color: "#fff" } }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <DialogContent sx={{ p: 0 }}>
                {/* Quick stats */}
                <Box sx={{ px: 3, py: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {[
                    { label: "Orders",      value: ros.length },
                    { label: "Restaurants", value: shopGroups.length },
                    { label: "Products",    value: totalProducts },
                    { label: "Total Value", value: `$${totalAmt.toFixed(2)}` },
                  ].map((s) => (
                    <Box key={s.label}>
                      <Typography fontSize={9} fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em">{s.label}</Typography>
                      <Typography fontSize={16} fontWeight={800} color="#0f172a">{s.value}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Restaurants list */}
                <Box sx={{ px: 3, pt: 2, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography fontSize={12} fontWeight={800} color="#0f172a" textTransform="uppercase" letterSpacing="0.06em">Assigned restaurants</Typography>
                  {shopGroups.length > 0 && (
                    <Button size="small"
                      onClick={() => setRoutePopupSelected(allChecked ? new Set() : new Set(shopGroups.map(o => o.shop_id)))}
                      sx={{ fontSize: 11, textTransform: "none", fontWeight: 700, color: "#0B1F3A" }}>
                      {allChecked ? "Clear all" : "Select all"}
                    </Button>
                  )}
                </Box>
                <TableContainer sx={{ maxHeight: 280 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ "& th": { bgcolor: "#f8fafc", fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", py: 1 } }}>
                        <TableCell padding="checkbox">
                          <input type="checkbox" checked={allChecked} ref={el => { if (el) el.indeterminate = someChecked; }}
                            onChange={() => setRoutePopupSelected(allChecked ? new Set() : new Set(shopGroups.map(o => o.shop_id)))} />
                        </TableCell>
                        <TableCell>Restaurant</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell align="right">Items</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Print</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shopGroups.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "#94a3b8", fontSize: 12 }}>No restaurants assigned to this route yet</TableCell></TableRow>
                      ) : shopGroups.map((o) => {
                        const checked = routePopupSelected.has(o.shop_id);
                        return (
                          <TableRow key={o.shop_id} hover sx={{ cursor: "pointer" }}
                            onClick={() => setRoutePopupSelected(prev => { const n = new Set(prev); n.has(o.shop_id) ? n.delete(o.shop_id) : n.add(o.shop_id); return n; })}>
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <input type="checkbox" checked={checked}
                                onChange={() => setRoutePopupSelected(prev => { const n = new Set(prev); n.has(o.shop_id) ? n.delete(o.shop_id) : n.add(o.shop_id); return n; })} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{o.shop_name}</TableCell>
                            <TableCell sx={{ fontSize: 12, color: "#475569" }}>{o.shop_city || "—"}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{o.item_count}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>${o.total.toFixed(2)}</TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Print this restaurant">
                                <IconButton size="small" onClick={() => printOrder(o, "order")}
                                  sx={{ width: 28, height: 28, color: "#475569", "&:hover": { color: "#0B1F3A", bgcolor: "#f1f5f9" } }}>
                                  <PrintIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Product summary */}
                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Typography fontSize={12} fontWeight={800} color="#0f172a" textTransform="uppercase" letterSpacing="0.06em">Product summary</Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 220 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ "& th": { bgcolor: "#f8fafc", fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", py: 1 } }}>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Total Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.values(productAgg).length === 0 ? (
                        <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3, color: "#94a3b8", fontSize: 12 }}>No products</TableCell></TableRow>
                      ) : Object.values(productAgg).sort((a, b) => b.qty - a.qty).map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{p.name}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: "#059669" }}>{p.qty}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </DialogContent>
              <Box sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", bgcolor: "#fafbfc" }}>
                <Button size="small" onClick={() => setRoutePopupId(null)}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, color: "#64748b" }}>
                  Close
                </Button>
                <Button size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                  onClick={() => printRoute(routePopupId!, Array.from(routePopupSelected))}
                  disabled={routePopupSelected.size === 0}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#0B1F3A", color: "#0B1F3A" } }}>
                  Print Selected ({routePopupSelected.size})
                </Button>
                <Button size="small" variant="contained" disableElevation startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                  onClick={() => printRoute(routePopupId!)}
                  disabled={ros.length === 0}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, bgcolor: "#0B1F3A", "&:hover": { bgcolor: "#1e3a5f" } }}>
                  Print Route
                </Button>
              </Box>
            </>
          );
        })()}
      </Dialog>

      {/* ═══ ROUTE MISMATCH CONFIRMATION ═══ */}
      <Dialog
        open={!!routeMismatch}
        onClose={() => !mismatchBusy && setRouteMismatch(null)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {routeMismatch && (() => {
          const { order, destRoute } = routeMismatch;
          const assignedNames = order.shop_route_ids
            .map(rid => routes.find(r => r.id === rid)?.name)
            .filter(Boolean) as string[];
          return (
            <>
              <Box sx={{ background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <WarningAmberIcon sx={{ fontSize: 22, color: "#92400e" }} />
                <Box>
                  <Typography fontSize={15} fontWeight={800} color="#78350f">Route Mismatch</Typography>
                  <Typography fontSize={11} color="#92400e">This restaurant isn't assigned to that route</Typography>
                </Box>
              </Box>
              <DialogContent sx={{ p: 3 }}>
                <Typography fontSize={13} color="#0f172a" mb={1.5}>
                  <Box component="span" fontWeight={800}>"{order.shop_name}"</Box> is currently assigned to:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                  {assignedNames.length === 0 ? (
                    <Typography fontSize={12} color="#94a3b8">No routes assigned yet</Typography>
                  ) : assignedNames.map((n) => (
                    <Chip key={n} label={n} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0" }} />
                  ))}
                </Box>
                <Typography fontSize={13} color="#0f172a" mb={1}>You are moving this order to:</Typography>
                <Chip label={destRoute.name} size="small" sx={{ height: 24, fontSize: 12, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", mb: 2 }} />
                <Typography fontSize={12} color="#64748b" lineHeight={1.6}>
                  <Box component="span" fontWeight={700}>Move Anyway</Box> — keep the move on today's planner only.<br />
                  <Box component="span" fontWeight={700}>Assign Route &amp; Move</Box> — also add this route to the restaurant's profile so it stays here on future days.
                </Typography>
              </DialogContent>
              <Box sx={{ px: 3, pb: 2.5, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Button size="small" disabled={!!mismatchBusy} onClick={() => handleMismatchAction("cancel")}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, color: "#64748b" }}>
                  Cancel
                </Button>
                <Button size="small" variant="outlined" disabled={!!mismatchBusy} onClick={() => handleMismatchAction("anyway")}
                  startIcon={mismatchBusy === "anyway" ? <CircularProgress size={12} /> : null}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: ACCENT, color: ACCENT } }}>
                  Move Anyway
                </Button>
                <Button size="small" variant="contained" disableElevation disabled={!!mismatchBusy} onClick={() => handleMismatchAction("assign")}
                  startIcon={mismatchBusy === "assign" ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : null}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, bgcolor: ACCENT, "&:hover": { bgcolor: "#1e3a5f" } }}>
                  Assign Route &amp; Move
                </Button>
              </Box>
            </>
          );
        })()}
      </Dialog>

      <Dialog open={!!viewOrder} onClose={() => setViewOrder(null)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", "@media (max-width:1100px)": { width: "92vw" } } }}>
        {viewOrder && (
          <>
            <Box sx={{ background: `linear-gradient(135deg, ${ACCENT}, #071521)`, px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography fontSize={16} fontWeight={700} color="#fff">{viewOrder.shop_name}</Typography>
                <Typography fontSize={11} color="rgba(255,255,255,0.6)">
                  Order #{viewOrder.id} · {viewOrder.item_count} item{viewOrder.item_count !== 1 ? "s" : ""} · ${viewOrder.total.toFixed(2)}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setViewOrder(null)} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: "#fff" } }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <DialogContent sx={{ p: 0 }}>
              {/* Order info */}
              <Box sx={{ px: 3, py: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 3, flexWrap: "wrap" }}>
                {[
                  { label: "Status", value: viewOrder.status },
                  { label: "City", value: viewOrder.shop_city || "—" },
                  { label: "Created", value: dayjs(viewOrder.created_at).format("MMM D, h:mm A") },
                ].map((info, i) => (
                  <Box key={i}>
                    <Typography fontSize={9} fontWeight={600} color="#94a3b8" textTransform="uppercase">{info.label}</Typography>
                    <Typography fontSize={13} fontWeight={600} color="#0f172a" sx={{ textTransform: "capitalize" }}>{info.value}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Assigned routes */}
              <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography fontSize={10} fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" sx={{ mr: 1 }}>Assigned routes:</Typography>
                {viewOrder.shop_route_ids.length === 0 ? (
                  <Typography fontSize={11} color="#94a3b8">No routes assigned to this restaurant</Typography>
                ) : viewOrder.shop_route_ids.map((rid) => {
                  const r = routes.find((rr) => rr.id === rid);
                  return r ? (
                    <Chip key={rid} icon={<AltRouteIcon sx={{ fontSize: 12 }} />} label={r.name} size="small"
                      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: alpha(ACCENT, 0.06), color: ACCENT, border: `1px solid ${alpha(ACCENT, 0.15)}` }} />
                  ) : null;
                })}
              </Box>

              {viewOrder.notes && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
                  <Typography fontSize={10} fontWeight={600} color="#92400e">Note: {viewOrder.notes}</Typography>
                </Box>
              )}
              {viewOrder.portal_note && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: "#f0f9ff", borderBottom: "1px solid #bae6fd" }}>
                  <Typography fontSize={10} fontWeight={600} color="#0369a1">Restaurant note: {viewOrder.portal_note}</Typography>
                </Box>
              )}

              {/* Items table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      {["#", "Product", "Qty", "Price", "Total"].map(h => (
                        <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", py: 1.25, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewOrder.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                          <Typography fontSize={12} color="#94a3b8">No items in this order</Typography>
                        </TableCell>
                      </TableRow>
                    ) : viewOrder.items.map((item, i) => (
                      <TableRow key={i} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell sx={{ py: 1.25, fontSize: 11, color: "#94a3b8" }}>{i + 1}</TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography fontSize={13} fontWeight={600} color="#0f172a">{item.name}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography fontSize={13} fontWeight={700} color="#0f172a">{item.quantity}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography fontSize={12} color="#64748b">${Number(item.price || 0).toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography fontSize={13} fontWeight={700} color="#059669">${(item.quantity * Number(item.price || 0)).toFixed(2)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Total footer */}
              <Box sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography fontSize={12} color="#64748b">{viewOrder.items.length} item{viewOrder.items.length !== 1 ? "s" : ""}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Button size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                    onClick={() => printOrder(viewOrder, "products")}
                    sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: ACCENT, color: ACCENT } }}>
                    Print Products
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                    onClick={() => printOrder(viewOrder, "order")}
                    sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: ACCENT, color: ACCENT } }}>
                    Print Order
                  </Button>
                  {viewOrder.shop_route_ids.length > 0 && (
                    <Button size="small" variant="contained" disableElevation startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                      onClick={() => viewOrder.shop_route_ids.forEach((rid) => printRoute(rid))}
                      sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: ACCENT, "&:hover": { bgcolor: "#1e3a5f" } }}>
                      Print Assigned Routes
                    </Button>
                  )}
                  <Box sx={{ textAlign: "right", ml: 1 }}>
                    <Typography fontSize={10} color="#94a3b8">Order Total</Typography>
                    <Typography fontSize={20} fontWeight={800} color="#0f172a" sx={{ fontFamily: "monospace" }}>${viewOrder.total.toFixed(2)}</Typography>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* ═══ PO PREVIEW MODAL ═══ */}
      <Dialog open={poPreviewOpen} onClose={() => setPOPreviewOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AddShoppingCartIcon sx={{ fontSize: 22, color: "#fff" }} />
            <Box>
              <Typography fontSize={16} fontWeight={700} color="#fff">Generate Purchase Orders</Typography>
              <Typography fontSize={11} color="rgba(255,255,255,0.5)">{poPreviewData.suppliers.length} supplier{poPreviewData.suppliers.length !== 1 ? "s" : ""} · {demandProducts.filter(p => p.shortage > 0).length} products</Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setPOPreviewOpen(false)} sx={{ color: "rgba(255,255,255,0.5)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {poPreviewData.noSupplier.length > 0 && (
            <Box sx={{ mx: 2.5, mt: 2, p: 1.5, borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a", display: "flex", alignItems: "flex-start", gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: "#d97706", mt: 0.25 }} />
              <Box>
                <Typography fontSize={11} fontWeight={700} color="#92400e">No supplier linked ({poPreviewData.noSupplier.length})</Typography>
                <Typography fontSize={10} color="#b45309">{poPreviewData.noSupplier.join(", ")}</Typography>
              </Box>
            </Box>
          )}
          {poPreviewData.suppliers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography fontSize={13} color="#94a3b8">No products with linked suppliers to create POs</Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2.5 }}>
              {poPreviewData.suppliers.map((group, gi) => (
                <Box key={gi} sx={{ mb: 2, borderRadius: 2.5, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: ROUTE_COLORS[gi % ROUTE_COLORS.length] }}>
                        <LocalShippingIcon sx={{ fontSize: 13, color: "#fff" }} />
                      </Box>
                      <Typography fontSize={13} fontWeight={700} color="#0f172a">{group.supplier_name}</Typography>
                    </Box>
                    <Typography fontSize={13} fontWeight={800} color="#059669">${group.total.toFixed(2)}</Typography>
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {group.items.map((item, ii) => (
                        <TableRow key={ii}>
                          <TableCell sx={{ fontSize: 12, fontWeight: 500, py: 1 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, py: 1 }}>{item.qty} {item.unit}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#64748b", py: 1 }}>@ ${item.price.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, py: 1 }}>${(item.qty * item.price).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Box>
          )}
          <Divider />
          <Box sx={{ display: "flex", gap: 1.5, p: 2.5, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={() => setPOPreviewOpen(false)}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>Cancel</Button>
            <Button variant="contained" disableElevation onClick={handleConfirmPOs}
              disabled={poCreating || poPreviewData.suppliers.length === 0}
              startIcon={poCreating ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#059669", "&:hover": { bgcolor: "#047857" } }}>
              {poCreating ? "Creating..." : `Create ${poPreviewData.suppliers.length} PO${poPreviewData.suppliers.length !== 1 ? "s" : ""}`}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Planner;
